import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['hiddenSelect', 'searchInput', 'dropdown', 'loading', 'noResults', 'optionsContainer', 'displayLabel', 'labelText']

  initialize() {
    this.searchTimeout = null
    this.isOpen = false
    this.options = []
    this.selectedValue = null
    this.selectedLabel = null
    this.cachedOptionsHtml = null
    this.lastRenderedSelectedValue = null
    this.currentPage = 1
    this.hasNextPage = false
    this.loadingMore = false
    this.onScrollHandler = null
  }

  connect() {
    // Get data attributes
    this.endpoint = this.element.dataset.endpoint
    this.preloadedOptions = this.element.dataset.options ? JSON.parse(this.element.dataset.options) : null
    this.valueField = this.element.dataset.valueField || 'id'
    this.labelField = this.element.dataset.labelField || 'name'
    this.dependentParam = this.element.dataset.dependentParam
    this.dependentParamName = this.element.dataset.dependentParamName
    this.otherDependentParam = this.element.dataset.otherDependentParam
    this.otherDependentParamName = this.element.dataset.otherDependentParamName
    this.placeholder = this.element.dataset.placeholder || 'Type to search...'

    // Set mode based on available data
    this.isStaticMode = !!this.preloadedOptions
    this.isAjaxMode = !!this.endpoint

    // Set initial values if present
    if (this.hiddenSelectTarget.value) {
      this.selectedValue = this.hiddenSelectTarget.value
      this.selectedLabel = this.hiddenSelectTarget.selectedOptions[0]?.text || ''

      // If we have a value but no label, we need to fetch it
      if (this.selectedValue && !this.selectedLabel) {
        this.fetchInitialLabel()
      } else {
        this.updateDisplayLabel()
      }
    }

    // Initialize options based on mode
    if (this.isStaticMode) {
      // Use preloaded options
      this.options = this.preloadedOptions
    } else if (this.isAjaxMode) {
      // Listen for changes in dependent parameters
      this.setupDependentListeners()

      // Pre-fetch options for instant first open (only if no dependencies)
      if (!this.dependentParam && !this.otherDependentParam) {
        this.prefetchOptions()
      }
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this))
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick.bind(this))
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    this.destroyDropdownScrollListener()
  }

  handleInput(event) {
    const query = event.target.value.trim()

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // If input is cleared, clear selection
    if (query === '') {
      this.clearSelection()
    }

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  showDropdown() {
    if (!this.isOpen) {
      this.isOpen = true

      // Position dropdown using fixed positioning
      const rect = this.displayLabelTarget.getBoundingClientRect()
      this.dropdownTarget.style.top = `${rect.bottom}px`
      this.dropdownTarget.style.left = `${rect.left}px`
      this.dropdownTarget.style.setProperty('--dropdown-width', `${rect.width}px`)

      // Show dropdown immediately
      this.dropdownTarget.classList.remove('hidden')

      // Focus and select the search input (if it exists)
      if (this.hasSearchInputTarget) {
        setTimeout(() => {
          this.searchInputTarget.focus()
          this.searchInputTarget.select()
        }, 10)
      }

      // If we have cached options, show them immediately
      if (this.options.length > 0) {
        this.renderOptionsOptimized()
      } else {
        // Show loading state immediately and fetch options
        this.showLoading()
        this.performSearch('')
      }
    }
  }

  hideDropdown() {
    this.isOpen = false
    this.destroyDropdownScrollListener()
    this.dropdownTarget.classList.add('hidden')
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  selectOption(event) {
    const option = event.currentTarget
    const value = option.dataset.value
    const label = option.dataset.label

    this.selectedValue = value
    this.selectedLabel = label

    // Update hidden select
    this.hiddenSelectTarget.innerHTML = `<option value="${value}" selected>${label}</option>`
    this.hiddenSelectTarget.value = value

    // Update display label and clear search input (if it exists)
    this.updateDisplayLabel()
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
    }

    // Reset options to show all (like initial state)
    this.resetOptionsToDefault()

    // Hide dropdown
    this.hideDropdown()

    // Trigger change event BEFORE resetting dependents so debounced submit is scheduled
    this.hiddenSelectTarget.dispatchEvent(new Event('change', { bubbles: true }))

    // Now reset dependent dropdowns (temporary isResettingDependents won't cancel the already scheduled submit)
    this.resetDependentDropdowns()
  }

  clearSelection() {
    this.selectedValue = null
    this.selectedLabel = null
    this.hiddenSelectTarget.innerHTML = ''
    this.hiddenSelectTarget.value = ''

    // Clear cached HTML since selection changed
    this.cachedOptionsHtml = null

    // Update display to show placeholder
    this.updateDisplayLabel()

    // Trigger change event
    this.hiddenSelectTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  updateDisplayLabel() {
    if (this.selectedLabel) {
      this.labelTextTarget.textContent = this.selectedLabel
      this.displayLabelTarget.classList.remove('text-gray-500')
    } else {
      this.labelTextTarget.textContent = this.placeholder
      this.displayLabelTarget.classList.add('text-gray-500')
    }
  }

  resetOptionsToDefault() {
    // Reset to show all options (like initial load)
    this.performSearch('')
  }

  performStaticSearch(query) {
    // Filter preloaded options based on query
    if (!query || query.trim() === '') {
      // Show all options when no query
      this.options = this.preloadedOptions
    } else {
      // Filter options based on label field
      const searchTerm = query.toLowerCase()
      this.options = this.preloadedOptions.filter(option => {
        const label = option[this.labelField]
        return label && label.toLowerCase().includes(searchTerm)
      })
    }

    // Render filtered options immediately
    this.renderOptions()
  }

  async prefetchOptions() {
    // Silently pre-fetch options in the background for instant first open
    try {
      const params = new URLSearchParams({
        type: 'select2',
        page: 1
      })

      const response = await fetch(`${this.endpoint}?${params}`)
      if (!response.ok) return

      const data = await response.json()
      const parsed = this.extractOptionsFromResponse(data)
      this.options = parsed.options
      this.hasNextPage = parsed.hasMore
      this.currentPage = 1
    } catch (error) {
      // Silently fail - user will just get normal loading behavior
      console.debug('Pre-fetch failed:', error)
    }
  }

  setupDependentListeners() {
    // Listen for changes in parent dropdowns that this dropdown depends on
    if (this.dependentParam) {
      const dependentElement = this.findDependentElement(this.dependentParam)
      if (dependentElement) {
        dependentElement.addEventListener('change', () => {
          this.handleDependentChange()
        })
      }
    }

    if (this.otherDependentParam) {
      const otherDependentElement = this.findDependentElement(this.otherDependentParam)
      if (otherDependentElement) {
        otherDependentElement.addEventListener('change', () => {
          this.handleDependentChange()
        })
      }
    }
  }

  handleDependentChange() {
    // Clear current selection and options
    this.clearSelection()

    // Clear options container
    const optionsContainer = this.optionsContainerTarget
    if (optionsContainer) {
      optionsContainer.innerHTML = ''
    }

    // Reset options array and cached HTML
    this.options = []
    this.cachedOptionsHtml = null
    this.currentPage = 1
    this.hasNextPage = false
    this.loadingMore = false
    this.destroyDropdownScrollListener()
  }

  resetDependentDropdowns() {
    // Scope resets to the current OR group's wrapper
    const wrapper = this.element.closest('[data-select-key]')
    const currentKey = wrapper ? wrapper.getAttribute('data-select-key') : ''
    if (!currentKey) return

    // Extract prefix (make_id/model_id/year/sub_model_id) and suffix (e.g., _3_1)
    const prefixes = ['make_id', 'model_id', 'year', 'sub_model_id']
    let prefix = null
    let suffix = ''
    for (const p of prefixes) {
      if (currentKey.startsWith(p)) {
        prefix = p
        suffix = currentKey.slice(p.length) // includes leading _clauseIndex[_termIndex]
        break
      }
    }
    if (!prefix) return

    // Notify advanced filter controller that we're starting dependent reset
    this.notifyAdvancedFilterController('start-reset')

    // Define the dependency chain for compatibility
    const dependencyChain = {
      'make_id': ['model_id', 'year', 'sub_model_id'],
      'model_id': ['year', 'sub_model_id'],
      'year': ['sub_model_id']
    }

    const dependentKeys = dependencyChain[prefix]
    if (!dependentKeys) {
      this.notifyAdvancedFilterController('end-reset')
      return
    }

    // Reset each dependent dropdown within the same group (suffix)
    dependentKeys.forEach(dependentKey => {
      const dependentWrapper = document.querySelector(`[data-select-key="${dependentKey}${suffix}"]`)
      if (dependentWrapper) {
        const searchableSelectElement = dependentWrapper.querySelector('[data-controller*="searchable-select"]')
        if (searchableSelectElement) {
          const hiddenSelect = searchableSelectElement.querySelector('select')
          const labelText = searchableSelectElement.querySelector('[data-searchable-select-target="labelText"]')

          if (hiddenSelect) {
            hiddenSelect.innerHTML = ''
            hiddenSelect.value = ''
            hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }))
          }

          if (labelText) {
            const placeholder = searchableSelectElement.dataset.placeholder || 'Type to search...'
            labelText.textContent = placeholder
            searchableSelectElement.querySelector('[data-searchable-select-target="displayLabel"]')?.classList.add('text-gray-500')
          }

          const optionsContainer = searchableSelectElement.querySelector('[data-searchable-select-target="optionsContainer"]')
          if (optionsContainer) {
            optionsContainer.innerHTML = ''
          }
        }
      }
    })

    // Notify advanced filter controller that we're done with dependent reset
    setTimeout(() => {
      this.notifyAdvancedFilterController('end-reset')
    }, 50)
  }

  // Notify the advanced filter controller about dependent reset state
  notifyAdvancedFilterController(action) {
    const advancedFilterForm = document.querySelector('[data-controller*="products--advanced-filter"]')
    if (advancedFilterForm) {
      const controller = this.application.getControllerForElementAndIdentifier(advancedFilterForm, 'products--advanced-filter')
      if (controller) {
        if (action === 'start-reset') {
          controller.isResettingDependents = true
          // Started dependent reset - form submissions paused
        } else if (action === 'end-reset') {
          controller.isResettingDependents = false
          // Ended dependent reset - form submissions resumed
        }
      }
    }
  }

  async fetchInitialLabel() {
    if (!this.endpoint || !this.selectedValue) return

    try {
      const params = new URLSearchParams({
        type: 'select2',
        page: 1
      })

      // Add dependent parameters if needed
      if (this.dependentParam) {
        const dependentElement = this.findDependentElement(this.dependentParam)
        if (dependentElement && dependentElement.value) {
          params.append(this.dependentParamName, dependentElement.value)
        }
      }

      if (this.otherDependentParam) {
        const otherDependentElement = this.findDependentElement(this.otherDependentParam)
        if (otherDependentElement && otherDependentElement.value) {
          params.append(this.otherDependentParamName, otherDependentElement.value)
        }
      }

      const response = await fetch(`${this.endpoint}?${params}`)
      if (!response.ok) return

      const data = await response.json()
      let options = []
      if (Array.isArray(data)) {
        options = data
      } else if (data && Array.isArray(data.results)) {
        options = data.results
      } else if (data && Array.isArray(data.data)) {
        options = data.data
      }

      // Find the option that matches our selected value
      const selectedOption = options.find(option => option[this.valueField] == this.selectedValue)
      if (selectedOption) {
        this.selectedLabel = selectedOption[this.labelField]
        this.updateDisplayLabel()

        // Update the hidden select with proper text
        this.hiddenSelectTarget.innerHTML = `<option value="${this.selectedValue}" selected>${this.selectedLabel}</option>`
      }
    } catch (error) {
      console.error('Failed to fetch initial label:', error)
      // Fallback to showing the value
      this.selectedLabel = this.selectedValue
      this.updateDisplayLabel()
    }
  }

  async performSearch(query) {
    if (this.isStaticMode) {
      this.performStaticSearch(query)
      return
    }

    if (!this.endpoint) {
      console.warn('No endpoint provided for searchable select')
      return
    }

    this.showLoading()
    this.currentPage = 1
    this.hasNextPage = false

    try {
      const params = new URLSearchParams({
        type: 'select2',
        page: 1
      })

      // Only add q parameter if query is not empty
      if (query && query.trim()) {
        params.append('q', query.trim())
      }

      // Add dependent parameters
      if (this.dependentParam) {
        const dependentElement = this.findDependentElement(this.dependentParam)
        if (dependentElement && dependentElement.value) {
          params.append(this.dependentParamName, dependentElement.value)
        }
      }

      if (this.otherDependentParam) {
        const otherDependentElement = this.findDependentElement(this.otherDependentParam)
        if (otherDependentElement && otherDependentElement.value) {
          params.append(this.otherDependentParamName, otherDependentElement.value)
        }
      }

      const response = await fetch(`${this.endpoint}?${params}`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      const parsed = this.extractOptionsFromResponse(data)
      this.options = parsed.options
      this.hasNextPage = parsed.hasMore
      this.currentPage = 1
      this.renderOptions()

    } catch (error) {
      console.error('Search failed:', error)
      this.showError('Search failed. Please try again.')
    } finally {
      this.hideLoading()
    }
  }

  findDependentElement(paramName) {
    // Look for element with data-select-key attribute
    return document.querySelector(`[data-select-key="${paramName}"] select`) ||
           document.querySelector(`[data-select-key="${paramName}"]`)
  }

  renderOptionsOptimized() {
    if (!this.options || this.options.length === 0) {
      this.showNoResults()
      return
    }

    // Only re-render if selection changed or no cached HTML
    if (!this.cachedOptionsHtml || this.lastRenderedSelectedValue !== this.selectedValue) {
      this.cachedOptionsHtml = this.generateOptionsHtml()
      this.lastRenderedSelectedValue = this.selectedValue
    }

    // Use cached HTML for instant display
    this.optionsContainerTarget.innerHTML = this.cachedOptionsHtml
    this.hideNoResults()
    if (this.isAjaxMode) this.setupDropdownScrollListener()
  }

  renderOptions() {
    if (!this.options || this.options.length === 0) {
      this.showNoResults()
      return
    }

    // Force re-render (used when options change)
    this.cachedOptionsHtml = this.generateOptionsHtml()
    this.lastRenderedSelectedValue = this.selectedValue
    this.optionsContainerTarget.innerHTML = this.cachedOptionsHtml
    this.hideNoResults()
    if (this.isAjaxMode) this.setupDropdownScrollListener()
  }

  generateOptionsHtml() {
    return this.options.map(option => {
      const value = option[this.valueField] || option.id || ''
      const label = option[this.labelField] || option.text || option.name || value || 'Unknown'
      const isSelected = this.selectedValue && this.selectedValue == value

      // Different styling for selected vs unselected options
      const baseClasses = "px-3 py-2 text-sm cursor-pointer transition-colors"
      const selectedClasses = isSelected
        ? "bg-blue-100 text-blue-800 border-l-4 border-blue-500 font-medium"
        : "hover:bg-blue-50 hover:text-blue-700"

      return `
        <div class="${baseClasses} ${selectedClasses}"
             data-action="click->searchable-select#selectOption"
             data-value="${value || ''}"
             data-label="${label || ''}">
          ${label}
          ${isSelected ? '<span class="ml-2 text-blue-600">✓</span>' : ''}
        </div>
      `
    }).join('')
  }

  setupDropdownScrollListener() {
    if (!this.isOpen || !this.isAjaxMode) return
    const c = this.optionsContainerTarget
    if (!c) return
    this.destroyDropdownScrollListener()
    if (!this.hasNextPage) return
    this.onScrollHandler = () => {
      if (!this.hasNextPage || this.loadingMore) return
      if (c.scrollTop + c.clientHeight >= c.scrollHeight - 50) this.loadMoreOptions()
    }
    c.addEventListener('scroll', this.onScrollHandler)
    if (c.scrollHeight <= c.clientHeight && this.hasNextPage && !this.loadingMore) {
      this.loadMoreOptions()
    }
  }

  destroyDropdownScrollListener() {
    const c = this.optionsContainerTarget
    if (c && this.onScrollHandler) c.removeEventListener('scroll', this.onScrollHandler)
    this.onScrollHandler = null
  }

  async loadMoreOptions() {
    if (this.loadingMore || !this.hasNextPage) return
    this.loadingMore = true
    const nextPage = (this.currentPage || 1) + 1

    try {
      const params = new URLSearchParams({
        type: 'select2',
        page: nextPage
      })

      const q = (this.searchInputTarget?.value || '').trim()
      if (q) params.append('q', q)

      if (this.dependentParam) {
        const dependentElement = this.findDependentElement(this.dependentParam)
        if (dependentElement && dependentElement.value) {
          params.append(this.dependentParamName, dependentElement.value)
        }
      }

      if (this.otherDependentParam) {
        const otherDependentElement = this.findDependentElement(this.otherDependentParam)
        if (otherDependentElement && otherDependentElement.value) {
          params.append(this.otherDependentParamName, otherDependentElement.value)
        }
      }

      const response = await fetch(`${this.endpoint}?${params}`)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      const parsed = this.extractOptionsFromResponse(data)

      if (parsed.options && parsed.options.length) {
        const container = this.optionsContainerTarget
        const { scrollTop } = container
        this.options = this.options.concat(parsed.options)
        this.cachedOptionsHtml = null
        this.renderOptions()
        container.scrollTop = scrollTop
        this.currentPage = nextPage
        this.hasNextPage = parsed.hasMore
      } else {
        this.hasNextPage = false
      }


    } catch (error) {
      console.error('Load more failed:', error)
    } finally {
      this.loadingMore = false
    }
  }

  extractOptionsFromResponse(data) {
    let options = []
    let hasMore = false

    if (Array.isArray(data)) {
      options = data
      hasMore = false
    } else if (data) {
      if (Array.isArray(data.results)) {
        options = data.results
        const p = data.pagination || {}
        hasMore = !!(p.more || p.has_more || p.hasNextPage)
      } else if (Array.isArray(data.data)) {
        options = data.data
        const m = data.meta || {}
        hasMore = !!(m.next_page || m.nextPage || (m.current_page && m.total_pages && m.current_page < m.total_pages) || data.has_next_page || data.has_more)
      } else if (Array.isArray(data.items)) {
        options = data.items
        hasMore = !!(data.next_page || data.nextPage || data.has_next_page)
      }
    }

    return { options, hasMore }
  }

  showLoading() {
    this.loadingTarget.classList.remove('hidden')
    this.hideNoResults()
  }

  hideLoading() {
    this.loadingTarget.classList.add('hidden')
  }

  showNoResults() {
    this.noResultsTarget.classList.remove('hidden')
    this.optionsContainerTarget.innerHTML = ''
  }

  hideNoResults() {
    this.noResultsTarget.classList.add('hidden')
  }

  showError(message) {
    this.optionsContainerTarget.innerHTML = `
      <div class="px-3 py-2 text-sm text-red-600">
        ${message}
      </div>
    `
  }
}
